import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'

import styles from './EmailInput.module.scss'

import { BulkInput } from '@/api/internal'
import TextArea from '@/components/TextArea'
import { parseEmailsWithValidation } from '@/utils/email'

export interface EmailValidationResult {
  input: BulkInput | null
  invalidEmails: string[]
}

export interface EmailInputHandle {
  getInput: () => BulkInput | null
  getValidatedInput: () => EmailValidationResult
  reset: () => void
  setEmails: (emails: string[]) => void
  hasInput: () => boolean
}

interface EmailInputProps {
  isDisabled?: boolean
  fileOnly?: boolean
}

const EmailInput = forwardRef<EmailInputHandle, EmailInputProps>(({ isDisabled, fileOnly }, ref) => {
  const [emailsInput, setEmailsInput] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const clearFileInput = useCallback(() => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const getValidatedInput = useCallback((): EmailValidationResult => {
    if (selectedFile) {
      return { input: { type: 'file', file: selectedFile }, invalidEmails: [] }
    }
    if (emailsInput.trim()) {
      const { valid, invalid } = parseEmailsWithValidation(emailsInput.trim())
      if (invalid.length > 0) {
        return { input: null, invalidEmails: invalid }
      }
      if (valid.length > 0) {
        return { input: { type: 'emails', emails: valid }, invalidEmails: [] }
      }
    }
    return { input: null, invalidEmails: [] }
  }, [selectedFile, emailsInput])

  const getInput = useCallback((): BulkInput | null => {
    return getValidatedInput().input
  }, [getValidatedInput])

  const reset = useCallback(() => {
    setEmailsInput('')
    clearFileInput()
  }, [clearFileInput])

  const hasInput = useCallback(() => {
    return !!(selectedFile || emailsInput.trim())
  }, [selectedFile, emailsInput])

  const setEmails = useCallback(
    (emails: string[]) => {
      setEmailsInput(emails.join('\n'))
      clearFileInput()
    },
    [clearFileInput]
  )

  useImperativeHandle(ref, () => ({ getInput, getValidatedInput, reset, hasInput, setEmails }), [
    getInput,
    getValidatedInput,
    reset,
    hasInput,
    setEmails,
  ])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setEmailsInput('')
    }
  }

  const handleTextareaChange = (value: string) => {
    setEmailsInput(value)
    if (value) {
      clearFileInput()
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.uploadForm}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.csv"
          onChange={handleFileSelect}
          className={styles.fileInput}
          disabled={isDisabled}
        />
        {selectedFile && <span className={styles.fileName}>{selectedFile.name}</span>}
      </div>
      {!fileOnly && (
        <>
          <div className={styles.orDivider}>
            <span>or</span>
          </div>
          <TextArea
            className={styles.emailsTextarea}
            placeholder={
              'Enter email addresses (separated by commas, semicolons, spaces, or new lines)\ne.g., user1@example.com, user2@example.com'
            }
            value={emailsInput}
            onInput={handleTextareaChange}
            rows={3}
            isDisabled={isDisabled}
          />
        </>
      )}
    </div>
  )
})

EmailInput.displayName = 'EmailInput'

export default EmailInput
